import React from 'react';
import { useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Container as SemanticContainer } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';

import { getSchema } from '@plone/volto/actions/schema/schema';
import { searchContent } from '@plone/volto/actions/search/search';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { isManager } from '@plone/volto/helpers/User/User';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import config from '@plone/volto/registry';

import { getLocations } from '../../../actions';
import useToast from '../../../helpers/useToast';
import { ActionComponent } from './ActionComponent';
import arrowRight from '../../../theme/icons/arrow-right.svg';
import Icon from '@plone/volto/components/theme/Icon/Icon';

import jwtDecode from 'jwt-decode';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import videoThumbnail from '../../../theme/images/video-thumbnail-default.png';
import GalleryComponent from './GalleryComponent';

/**
 * LocationView view component class.
 * @function LocationView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const LocationView = ({ content, token, intl, location, pathname }) => {
  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;
  const dispatch = useDispatch();
  const history = useHistory();

  const locations_get = useSelector((state) => state.locations.get);
  const location_add = useSelector((state) => state.location.add);
  const prevlocation_add = usePrevious(location_add);
  const user = useSelector((state) => state.users.user);
  const userId = token ? jwtDecode(token).sub : '';
  const isLoggedIn = !!userId;
  const folderPath = pathname
    ? pathname.replace(/\/edit$/, '')
    : location?.pathname;
  const images = useSelector(
    (state) => state?.search?.subrequests?.images?.items,
  );
  const gallery_id = 'photoswipeGallery';

  React.useEffect(() => {
    isLoggedIn &&
      dispatch(
        searchContent(
          folderPath,
          {
            'path.depth': 2,
            portal_type: ['Image', 'File'],
            sort_on: 'getObjPositionInParent',
          },
          'images',
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  // const [items, setItems] = useState(null);
  // eslint-disable-next-line no-unused-vars

  useEffect(() => {
    isLoggedIn &&
      dispatch(
        getLocations(
          getBaseUrl(location.pathname).split('/').slice(0, 2).join('/'),
        ),
      );
  }, [dispatch, isLoggedIn, location.pathname]);
  useEffect(() => {
    prevlocation_add?.loading &&
      location_add.loaded &&
      dispatch(
        getLocations(
          getBaseUrl(location.pathname).split('/').slice(0, 2).join('/'),
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location_add.loaded, prevlocation_add?.loading]);

  /** [START] PROBABLY WILL BE DELETED */
  React.useEffect(() => {
    content?.['@type'] &&
      dispatch(getSchema(content['@type'], location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const contentLoaded = content && !isEqual(Object.keys(content), ['lock']);
  /** [END] PROBABLY WILL BE DELETED */
  const Image = config.getComponent({ name: 'Image' }).component;

  const queryParams = new URLSearchParams(location.search);
  return (
    <>
      {useToast('location.add')}
      {contentLoaded ? (
        <Container
          id="page-document"
          className="view-wrapper
          location-view"
        >
          {content.image && (
            <Image
              className="main-image"
              item={content}
              sizes="(min-width: 1400px) 1500px, 90vw"
              alt={content.title || ''}
              title={content.title || ''}
              responsive={true}
            />
          )}
          <div className="back-button-container">
            {!queryParams.get('paged') ? (
              <UniversalLink
                item={content.parent}
                className="block __button has--button-color--white"
              >
                <button className="ui button ">
                  <Icon
                    className="arrow svg-inline--fa rotate-180"
                    name={arrowRight}
                    title="arrow"
                  />
                  <FormattedMessage
                    id="Go to locations list"
                    defaultMessage="Go to locations list"
                  />
                </button>
              </UniversalLink>
            ) : (
              <div className="block __button has--button-color--white">
                <button className="ui button" onClick={() => history.goBack()}>
                  <Icon
                    className="arrow svg-inline--fa rotate-180"
                    name={arrowRight}
                    title="arrow"
                  />
                  <FormattedMessage
                    id="Go to locations list"
                    defaultMessage="Go to locations list"
                  />
                </button>
              </div>
            )}
          </div>
          <div className="columns-view">
            <div className="ui twelve column grid column-grid">
              <div className="six wide computer twelve wide mobile twelve wide tablet column location-data-container">
                {content.title && <h1>{content.title}</h1>}
                <ul className="tags">
                  {content?.taxonomy_region?.map((item, key) => (
                    <li key={key} className="ui label">
                      {item.title}
                    </li>
                  ))}
                  {content?.taxonomy_type?.map((item, key) => (
                    <li key={key} className="ui label">
                      {item.title}
                    </li>
                  ))}
                  {content?.taxonomy_context?.map((item, key) => (
                    <li key={key} className="ui label">
                      {item.title}
                    </li>
                  ))}
                </ul>
                {content.items?.length > 0 && isManager(user) && (
                  <div>
                    <UniversalLink
                      className="ui button"
                      item={content.items[0]}
                    >
                      <FormattedMessage
                        id="Edit private data"
                        defaultMessage="Edit private data"
                      />
                    </UniversalLink>
                  </div>
                )}
                <ActionComponent
                  content={content}
                  locations={locations_get.data}
                  pathname={location.pathname}
                  isLoggedIn={isLoggedIn}
                  isManager={isManager(user)}
                  intl={intl}
                  dispatch={dispatch}
                />
                {isLoggedIn &&
                  content.items?.filter((i) => i?.['@type'] === 'LocationBlock')
                    ?.length > 0 && (
                    <div className="location-private-data">
                      <RenderBlocks content={content.items[0]} />
                    </div>
                  )}
              </div>
              <div className="six wide computer twelve wide mobile twelve wide tablet column ">
                <div
                  dangerouslySetInnerHTML={{ __html: content.location_map }}
                ></div>
                <div className="pswp-gallery" id={gallery_id}>
                  {isLoggedIn && content.items?.length > 0 && (
                    <GalleryComponent
                      images={images}
                      videoThumbnail={videoThumbnail}
                      isLoggedIn={isLoggedIn}
                      gallery_id={gallery_id}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
      ) : null}
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
LocationView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    for_member: PropTypes.string,
    for_authorized_members: PropTypes.string,
    for_requested_members: PropTypes.string,
  }).isRequired,
};
export default injectIntl(LocationView);
